import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import appApi from '../../../../api/app-api';
import dateUtils from '../../../../utils/date-format';
import {
  getLectureStatusDisplayText,
  LectureStatus,
} from '../../../../api/types/programs/course/lectures/lecture-status.type';
import { toast } from 'react-toastify';
import { LecturesResType } from '../../../lectures/operations/get-lectures-data-operation';
import { useNavigate } from 'react-router-dom';
import '../home-lectures-widgets.css';

export const HistoricalInitLecturesHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<LecturesResType[]>([]);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const defaultRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 1);
    return { start, end };
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | null>(defaultRange.end);

  const fetchData = async () => {
    if (!startDate) {
      toast.warn('יש לבחור תאריך התחלה');
      return;
    }

    setLoading(true);
    try {
      const res = await appApi.getLectures({
        branchesIds: [],
        programs: [],
        startYears: [],
        classes: [],
        planDateRangeStart: startDate,
        planDateRangeEnd: endDate,
        lectureState: [LectureStatus.INIT],
        courses: [],
        courseStatuses: [],
        offset: 0,
        limit,
      });

      if (res.data.errCode) {
        toast.warn('לא ניתן לטעון נתונים');
        setData([]);
        setTotal(0);
        return;
      }

      const body = res.data.body;
      const rows = body?.data ?? [];
      setData(Array.isArray(rows) ? rows : []);
      setTotal(body?.total ?? 0);
    } catch (e) {
      toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusTemplate = (row: LecturesResType) => {
    const txt = getLectureStatusDisplayText(row.lectureStatus as any);
    return <Tag value={txt} severity="warning" />;
  };

  return (
    <div
      className="home-lecture-widget"
      style={{
        backgroundColor: '#ffffff',
        padding: '20px',
        height: '100%',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '10px',
          flexDirection: 'row',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            textAlign: 'right',
          }}
        >
          <div
            style={{
              color: '#185FD3',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexDirection: 'row-reverse',
              gap: '8px',
            }}
          >
            <span>מפגשים היסטוריים שלא בוצעו </span>
            <i className="pi pi-history" />
          </div>
          <div style={{ color: '#6B7280', fontSize: '13px' }}>
            מציג מפגשים שתוכננו אך לא בוצעו בטווח התאריכים שנבחר. ניתן לרענן או
            לעבור לדו״ח המלא.
          </div>

          <div
            style={{
              marginTop: '8px',
              background: '#EEF2FF',
              border: '1px solid #C7D2FE',
              color: '#1E3A8A',
              borderRadius: '10px',
              padding: '8px 10px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className="pi pi-info-circle" style={{ marginTop: '2px' }} />
            <span>
              מוצגת כאן תצוגה מקדימה (עד {limit} רשומות). לדו״ח המלא עם כל
              הנתונים והסינונים יש ללחוץ על “לדו״ח המלא”.
            </span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-start',
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                מתאריך
              </span>
              <Calendar
                value={startDate}
                onChange={(e) => setStartDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '170px', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl' }}
              />
            </div>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <span
                style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textAlign: 'right',
                }}
              >
                עד תאריך
              </span>
              <Calendar
                value={endDate}
                onChange={(e) => setEndDate(e.value as Date)}
                dateFormat="dd/mm/yy"
                showIcon
                iconPos="right"
                style={{ width: '170px', direction: 'ltr' }}
                inputStyle={{ direction: 'rtl' }}
                minDate={startDate || undefined}
              />
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-start',
              whiteSpace: 'nowrap',
            }}
          >
            <Button
              icon="pi pi-refresh"
              label="רענן"
              size="small"
              onClick={() => {
                fetchData();
              }}
              loading={loading}
            />
            <Button
              icon="pi pi-external-link"
              label="לדו״ח המלא"
              size="small"
              tooltip={'לחץ למעבר לדו״ח'}
              severity="secondary"
              אםם
              outlined
              onClick={() => navigate('/education/lectures')}
            />
          </div>
        </div>
      </div>

      <div style={{ fontSize: '13px', color: '#4b5563', textAlign: 'right' }}>
        סה״כ רשומות: {total.toLocaleString()}
      </div>

      <div style={{ marginTop: '10px', flex: 1 }}>
        <DataTable
          value={data}
          loading={loading}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          scrollHeight="280px"
          scrollable
          dataKey="lectureSubject"
          tableStyle={{ direction: 'rtl' }}
          rowHover
        >
          <Column
            field="branchName"
            header="שלוחה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="abstractProgramName"
            header="תוכנית"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="courseName"
            header="קורס"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            field="additionalCourseName"
            header="שם קורס נוסף"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(
              row: LecturesResType & { additionalCourseName?: string | null },
            ) => row.additionalCourseName || '-'}
          />
          <Column
            field="className"
            header="כיתה"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            header="תאריך"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: LecturesResType) =>
              row.lecturers?.[0]?.workingStartTime
                ? dateUtils.displayFormatDate(row.lecturers[0].workingStartTime)
                : '-'
            }
          />
          <Column
            header="שעות"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: LecturesResType) =>
              row.lecturers?.[0]?.workingStartTime &&
              row.lecturers?.[0]?.workingEndTime
                ? dateUtils.formatTimeRangeBetweenDates(
                    row.lecturers[0].workingStartTime,
                    row.lecturers[0].workingEndTime,
                  )
                : '-'
            }
          />
          <Column
            field="lectureStatus"
            header="סטטוס"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
            body={statusTemplate}
          />
        </DataTable>
      </div>
    </div>
  );
};
